import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grow,
    IconButton,
    InputAdornment,
    TextField,
    Tooltip,
    useMediaQuery,
    useTheme
} from '@material-ui/core';
import {makeStyles} from '@material-ui/core/styles';
import Skeleton from '@material-ui/lab/Skeleton';
import AddIcon from '@material-ui/icons/Add';
import EditIcon from '@material-ui/icons/Edit';
import {MuiPickersUtilsProvider, TimePicker} from '@material-ui/pickers';
import DateFnsUtils from '@date-io/date-fns';
import MUIDataTable from 'mui-datatables';
import {useSnackbar} from 'notistack';
import API_Service, {axiosURIencode} from '../../../../Services/API_Service';
import {req_working_shift_list, req_working_shift_save, req_working_shift_status} from '../../../../Helper/Constants';
import {SectionToolbar, SettingsSurface, StatusSwitch, StatusTabs} from './ui';

const useStyles = makeStyles((theme) => ({
    addButton: {
        width: 40,
        height: 40,
        color: theme.palette.getContrastText(theme.palette.primary.main),
        backgroundColor: theme.palette.primary.main,
        boxShadow: '0 7px 16px rgba(0,0,0,.16)',
        transition: 'transform 140ms cubic-bezier(.34,1.3,.64,1)',
        '&:hover': {backgroundColor: theme.palette.primary.main, transform: 'translateY(-2px) scale(1.04)'}
    },
    tableSurface: {
        '& .MuiPaper-root': {boxShadow: 'none', backgroundColor: 'transparent'},
        '& .MuiTableCell-head': {fontSize: 11, fontWeight: 800, letterSpacing: '.05em', textTransform: 'uppercase'},
        '& .MuiTableRow-root': {transition: 'background-color 120ms ease'},
        '& .MuiTableRow-root:hover': {backgroundColor: theme.palette.action.hover},
        '& .MUIDataTableToolbar-root': {display: 'none'}
    },
    dialogPaper: {borderRadius: 16},
    title: {fontWeight: 800},
    form: {
        display: 'grid',
        gap: theme.spacing(2.5),
        padding: theme.spacing(1, 0, 0.5)
    },
    timeGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
        gap: theme.spacing(2),
        [theme.breakpoints.down('xs')]: {gridTemplateColumns: '1fr'}
    },
    field: {'& .MuiOutlinedInput-root': {borderRadius: 11}},
    statusRow: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: theme.spacing(2),
        [theme.breakpoints.down('xs')]: {alignItems: 'stretch', flexDirection: 'column'}
    },
    loader: {padding: theme.spacing(1, 2)}
}));

const parseShiftTime = (timeStr) => {
    if (!timeStr) return null;
    const parts = String(timeStr).split(':');
    const date = new Date();
    date.setHours(parseInt(parts[0]) || 0, parseInt(parts[1]) || 0, parseInt(parts[2]) || 0, 0);
    return date;
};

const formatShiftTime = (date) => {
    if (!date) return '';
    const pad = (number) => String(number).padStart(2, '0');
    return `${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
};

const formatTimeDisplay = (timeStr) => {
    if (!timeStr) return '';
    const parts = String(timeStr).split(':');
    const hour = parseInt(parts[0]) || 0;
    const minute = parseInt(parts[1]) || 0;
    return `${hour % 12 === 0 ? 12 : hour % 12}:${String(minute).padStart(2, '0')} ${hour >= 12 ? 'PM' : 'AM'}`;
};

export default function WorkingShiftSection({axiosConfig, axiosPut, language}) {
    const classes = useStyles();
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('xs'));
    const {enqueueSnackbar} = useSnackbar();
    const [items, setItems] = useState(null);
    const [statusFilter, setStatusFilter] = useState(1);
    const [search, setSearch] = useState('');
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editId, setEditId] = useState(null);
    const [name, setName] = useState('');
    const [startTime, setStartTime] = useState(null);
    const [endTime, setEndTime] = useState(null);
    const [payout, setPayout] = useState('');
    const [status, setStatus] = useState(1);
    const [saving, setSaving] = useState(false);

    const load = useCallback(() => {
        setItems(null);
        API_Service.get(req_working_shift_list, axiosConfig).then(({data}) => {
            if (parseInt(data.result) === 1) setItems(data.extraData || []);
            else {
                setItems([]);
                enqueueSnackbar(data.message || language.operation_fail, {variant: 'error'});
            }
        }).catch(() => {
            setItems([]);
            enqueueSnackbar(language.operation_fail || 'Network error', {variant: 'error'});
        });
    }, [axiosConfig, enqueueSnackbar, language.operation_fail]);

    useEffect(() => { load(); }, [load]);

    const filtered = useMemo(() => (items || [])
        .filter((item) => parseInt(item.status) === parseInt(statusFilter))
        .filter((item) => !search || String(item.shift_name || '').toLowerCase().includes(search.toLowerCase())), [items, search, statusFilter]);

    const resetDialog = () => {
        setDialogOpen(false);
        setEditId(null);
        setName('');
        setStartTime(null);
        setEndTime(null);
        setPayout('');
        setStatus(1);
    };

    const openDialog = (item) => {
        if (item) {
            setEditId(item.id);
            setName(item.shift_name || '');
            setStartTime(parseShiftTime(item.start_time));
            setEndTime(parseShiftTime(item.end_time));
            setPayout(item.payout != null ? String(item.payout) : '');
            setStatus(parseInt(item.status) === 1 ? 1 : 0);
        } else {
            setEditId(null);
            setName('');
            setStartTime(null);
            setEndTime(null);
            setPayout('');
            setStatus(1);
        }
        setDialogOpen(true);
    };

    const save = () => {
        if (!name.trim() || !startTime || !endTime) {
            enqueueSnackbar(language.required_fields || language.no_data, {variant: 'error'});
            return;
        }
        if (saving) return;
        setSaving(true);
        const formdata = new FormData();
        if (editId) formdata.append('id', editId);
        formdata.append('shift_name', name.trim());
        formdata.append('start_time', formatShiftTime(startTime));
        formdata.append('end_time', formatShiftTime(endTime));
        formdata.append('payout', payout === '' ? 0 : payout);
        formdata.append('status', status);
        API_Service.post(req_working_shift_save, formdata, axiosConfig).then(({data}) => {
            setSaving(false);
            if (parseInt(data.result) === 1) {
                enqueueSnackbar(data.message || language.operation_success, {variant: 'success'});
                resetDialog();
                load();
            } else enqueueSnackbar(data.message || language.operation_fail, {variant: 'error'});
        }).catch(() => {
            setSaving(false);
            enqueueSnackbar(language.operation_fail || 'Network error', {variant: 'error'});
        });
    };

    const [togglingId, setTogglingId] = useState(null);

    const toggleStatus = (item) => {
        setTogglingId(item.id);
        API_Service.put(req_working_shift_status, axiosURIencode({id: item.id}), axiosPut).then(({data}) => {
            if (parseInt(data.result) === 1) {
                enqueueSnackbar(data.message || language.operation_success, {variant: 'success'});
                load();
            } else enqueueSnackbar(data.message || language.operation_fail, {variant: 'error'});
        }).catch(() => enqueueSnackbar(language.operation_fail || 'Network error', {variant: 'error'}))
            .finally(() => setTogglingId(null));
    };

    const columns = [
        {name: 'shift_name', label: language.shift_name, options: {sort: true}},
        {name: 'start_time', label: language.start_time, options: {customBodyRender: formatTimeDisplay}},
        {name: 'end_time', label: language.end_time, options: {customBodyRender: formatTimeDisplay}},
        {name: 'payout', label: language.payout, options: {customBodyRender: (value) => `RM ${parseFloat(value || 0).toFixed(2)}`}},
        {name: 'total_hours', label: language.total_hours, options: {customBodyRender: (value) => parseFloat(value || 0).toFixed(2)}},
        {
            name: 'action',
            label: language.action,
            options: {
                sort: false,
                customBodyRender: (value, tableMeta) => {
                    const item = filtered[tableMeta.rowIndex];
                    if (!item) return null;
                    const active = parseInt(item.status) === 1;
                    return (
                        <React.Fragment>
                            <Tooltip title={language.edit}>
                                <IconButton size="small" onClick={(event) => { event.stopPropagation(); openDialog(item); }}><EditIcon fontSize="small"/></IconButton>
                            </Tooltip>
                            <StatusSwitch active={active} pending={String(togglingId) === String(item.id)} name={item.shift_name} language={language} onToggle={() => toggleStatus(item)}/>
                        </React.Fragment>
                    );
                }
            }
        }
    ];

    return (
        <React.Fragment>
            <SectionToolbar search={search} onSearch={setSearch} placeholder={language.search}>
                <StatusTabs value={statusFilter} onChange={setStatusFilter} activeLabel={language.active} inactiveLabel={language.inactive}/>
                <Tooltip title={language.add_working_shift}>
                    <IconButton className={classes.addButton} onClick={() => openDialog(null)} aria-label={language.add_working_shift}><AddIcon/></IconButton>
                </Tooltip>
            </SectionToolbar>
            <SettingsSurface className={classes.tableSurface}>
                {items === null ? (
                    <div className={classes.loader}>{[0, 1, 2, 3].map((row) => <Skeleton key={row} animation="wave" height={54}/>)}</div>
                ) : (
                    <MUIDataTable
                        title=""
                        data={filtered}
                        columns={columns}
                        options={{
                            selectableRows: 'none',
                            responsive: 'standard',
                            rowsPerPage: 20,
                            rowsPerPageOptions: [20, 50, 100],
                            print: false,
                            download: false,
                            filter: false,
                            search: false,
                            viewColumns: false,
                            elevation: 0,
                            textLabels: {body: {noMatch: language.no_data}}
                        }}
                    />
                )}
            </SettingsSurface>
            <Dialog
                open={dialogOpen}
                onClose={() => { if (!saving) resetDialog(); }}
                fullScreen={fullScreen}
                fullWidth
                maxWidth="sm"
                TransitionComponent={Grow}
                PaperProps={{className: classes.dialogPaper}}
            >
                <DialogTitle className={classes.title}>{editId ? language.edit_working_shift : language.add_working_shift}</DialogTitle>
                <DialogContent>
                    <div className={classes.form}>
                        <TextField
                            className={classes.field}
                            autoFocus
                            fullWidth
                            required
                            variant="outlined"
                            label={language.shift_name}
                            value={name}
                            onChange={(event) => setName(event.target.value)}
                            inputProps={{maxLength: 255}}
                        />
                        <MuiPickersUtilsProvider utils={DateFnsUtils}>
                            <div className={classes.timeGrid}>
                                <TimePicker className={classes.field} fullWidth required inputVariant="outlined" ampm label={language.start_time} value={startTime} onChange={setStartTime}/>
                                <TimePicker className={classes.field} fullWidth required inputVariant="outlined" ampm label={language.end_time} value={endTime} onChange={setEndTime}/>
                            </div>
                        </MuiPickersUtilsProvider>
                        <TextField
                            className={classes.field}
                            fullWidth
                            variant="outlined"
                            type="number"
                            label={language.payout}
                            value={payout}
                            onChange={(event) => setPayout(event.target.value)}
                            InputProps={{startAdornment: <InputAdornment position="start">RM</InputAdornment>, inputProps: {min: 0, step: 0.01}}}
                        />
                        <div className={classes.statusRow}>
                            <span>{language.status || 'Status'}</span>
                            <StatusTabs value={status} onChange={setStatus} activeLabel={language.active} inactiveLabel={language.inactive}/>
                        </div>
                    </div>
                </DialogContent>
                <DialogActions>
                    <Button disabled={saving} onClick={resetDialog}>{language.cancel}</Button>
                    <Button variant="contained" color="primary" disabled={saving} onClick={save}>
                        {saving ? <CircularProgress size={18}/> : editId ? language.update : language.save}
                    </Button>
                </DialogActions>
            </Dialog>
        </React.Fragment>
    );
}
